'use client'

import { useState, useEffect, useCallback, Suspense, lazy } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useUser } from '@/hooks/useUser'
import { useClientData } from '@/hooks/useClientData'
import { ClientTab, PropertyWithLandlord } from '@/types/client'
import { Property, User } from '@/types'
import VerificationBadge from '@/components/landlord/VerificationBadge'
import UserProfile from '@/components/landlord/UserProfile'
import ApplicationForm from '@/components/client/ApplicationForm'
import ApplicationStatus from '@/components/client/ApplicationStatus'
import SavedProperties from '@/components/client/SavedProperties'
import PropertySearch from '@/components/client/PropertySearch'
import PaymentInterface from '@/components/client/PaymentInterface'
import { config } from '@/lib/config'
import KycStepper from '@/components/client/KycStepper'
import ClientSidebar from '@/components/client/ClientSidebar'
import NotificationSystem from '@/components/client/NotificationSystem'
// Using regular img tag instead of Next.js Image component
import CurrencySwitcher from '@/components/client/CurrencySwitcher'
import NotificationPreferences from '@/components/NotificationPreferences'
import { useCurrency } from '@/lib/currency'
import EmptyState from '@/components/EmptyState'
import OnboardingFlow from '@/components/OnboardingFlow'
import { PropertyListSkeleton } from '@/components/SkeletonLoader'
import ReviewRatingSystem from '@/components/client/ReviewRatingSystem'
import MyTenancies from '@/components/client/MyTenancies'
import { getApiUrl } from '@/lib/apiUrl'
// Stripe.js not needed - we use direct URL redirect

// Lazy load PaymentHistory component for better performance
const PaymentHistory = lazy(() => import('@/components/client/PaymentHistory'))
const PaymentHistorySkeleton = lazy(() => import('@/components/client/PaymentHistorySkeleton'))

function ClientDashboardContent() {
  const { format } = useCurrency()
  const { user, loading: userLoading, refreshUser } = useUser()
  const { logout } = useAuth()
  const navigate = useNavigate()

  // State for UI
  const [activeTab, setActiveTab] = useState<ClientTab>('browse')
  const [showApplicationForm, setShowApplicationForm] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null)
  const [savingProperty, setSavingProperty] = useState<string | null>(null)
  const [acceptedByPropertyId, setAcceptedByPropertyId] = useState<Record<string, string>>({})
  const [paymentStatusByPropertyId, setPaymentStatusByPropertyId] = useState<Record<string, { paid: boolean; escrowStatus?: string }>>({})
  const [displayedProperties, setDisplayedProperties] = useState<PropertyWithLandlord[]>([])

  // Get data using custom hook
  const {
    properties,
    savedProperties,
    tenancies,
    loading,
    stats,
    loadTenancies,
    toggleSavedProperty,
    refreshAll
  } = useClientData({
    userId: user?.id || ''
  })

  // Load accepted applications for properties
  const loadAcceptedForProperties = useCallback(async (props: PropertyWithLandlord[]) => {
    if (!user || props.length === 0) {
      setAcceptedByPropertyId({})
      return
    }
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setAcceptedByPropertyId({})
        return
      }

      const propertyIds = props.map(p => p.id)
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/applications?status=approved&propertyIds=${propertyIds.join(',')}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // Handle both array and object with applications property
        const applicationsArray = Array.isArray(data) ? data : (data.applications || [])
        const map: Record<string, string> = {}
        applicationsArray.forEach((app: any) => {
          const propertyId = app.property?._id || app.property?.id || app.property_id || app.property
          const appId = app._id || app.id
          if (propertyId && appId) {
            map[propertyId.toString()] = appId.toString()
          }
        })
        setAcceptedByPropertyId(map)
      } else {
        console.error('Error loading accepted applications:', response.statusText)
        setAcceptedByPropertyId({})
      }
    } catch (e) {
      console.error('Error:', e)
      setAcceptedByPropertyId({})
    }
  }, [user])

  // Initialize displayed properties with loaded properties from useClientData
  // This is a fallback if PropertySearch hasn't populated displayedProperties yet
  useEffect(() => {
    // Only set if PropertySearch hasn't populated displayedProperties yet
    // PropertySearch will override this when it loads
    if (properties.length > 0 && displayedProperties.length === 0) {
      console.log('Initializing displayedProperties from useClientData:', properties.length, 'properties')
      setDisplayedProperties(properties)
    }
  }, [properties, displayedProperties.length])

  // Keep accepted map in sync with current properties
  useEffect(() => {
    const propsToCheck = displayedProperties.length > 0 ? displayedProperties : properties
    loadAcceptedForProperties(propsToCheck)
  }, [displayedProperties, properties, user, loadAcceptedForProperties])

  // Check payment status for accepted applications
  useEffect(() => {
    const checkPayments = async () => {
      const propertyIds = Object.keys(acceptedByPropertyId)
      if (propertyIds.length === 0) return

      const token = localStorage.getItem('authToken')
      if (!token) return

      const apiUrl = getApiUrl()
      const statusMap: Record<string, { paid: boolean; escrowStatus?: string }> = {}

      await Promise.all(
        propertyIds.map(async (propertyId) => {
          const appId = acceptedByPropertyId[propertyId]
          try {
            const paymentResponse = await fetch(`${apiUrl}/payments/history?applicationId=${appId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            })
            if (paymentResponse.ok) {
              const paymentData = await paymentResponse.json()
              const payments = paymentData.payments || []
              const hasCompletedPayment = payments.some((p: any) => p.status === 'completed' || p.status === 'succeeded')
              const escrowStatus = hasCompletedPayment 
                ? payments.find((p: any) => p.status === 'completed' || p.status === 'succeeded')?.escrowStatus || null
                : null
              statusMap[propertyId] = {
                paid: hasCompletedPayment,
                escrowStatus: escrowStatus || undefined
              }
            } else if (paymentResponse.status === 429) {
              // Rate limited - log warning but don't throw error
              console.warn('Rate limited when checking payment status. Will retry later.')
            }
          } catch (e) {
            console.error('Error checking payment:', e)
          }
        })
      )

      setPaymentStatusByPropertyId(statusMap)
    }

    checkPayments()
  }, [acceptedByPropertyId])

  // Periodic refresh of user data to catch verification status changes (2 min to reduce rate-limit pressure)
  useEffect(() => {
    if (!user?.id) return
    
    const refreshInterval = setInterval(() => {
      refreshUser()
    }, 120000) // Refresh every 2 minutes
    
    return () => clearInterval(refreshInterval)
  }, [user?.id, refreshUser])

  // Load tenancies when My Tenancies tab is active
  useEffect(() => {
    if (activeTab === 'tenancies' && user?.id) {
      loadTenancies()
    }
  }, [activeTab, user?.id, loadTenancies])

  // Check if user has the correct role
  if (!userLoading && user && user.role !== 'client') {
    navigate('/auth')
    return null
  }

  // Start Stripe Checkout for accepted application
  const startStripePayment = async (applicationId: string, property: Property) => {
    try {
      if (property.is_available === false) {
        alert('This property is currently unavailable.')
        return
      }

      // Validate amount
      const amount = property.price || 0
      if (!amount || amount <= 0) {
        alert('Invalid payment amount. Please contact support.')
        console.error('Invalid amount:', amount)
        return
      }

      // Check authentication
      const token = localStorage.getItem('authToken')
      
      if (!token) {
        alert('Please log in to make a payment')
        return
      }

      const apiUrl = getApiUrl()
      const authResponse = await fetch(`${apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (!authResponse.ok) {
        alert('Please log in to make a payment')
        return
      }

      // Create Stripe session through backend (same origin in dev via proxy)
      let res: Response
      try {
        res = await fetch(`${apiUrl}/payments/create-checkout`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
        body: JSON.stringify({
          applicationId,
          amount: amount,
          currency: (localStorage.getItem('landlordnoagent:currency') === 'NGN' ? 'ngn' : 'ngn'),
        })
        })
      } catch (fetchError: any) {
        console.error('Network error creating payment session:', fetchError)
        alert(`Network error: ${fetchError.message || 'Unable to connect to payment server. Please check your internet connection and try again.'}`)
        return
      }
      
      if (!res.ok) {
        let errorData: any = { message: 'Failed to create payment session' }
        try {
          const contentType = res.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            errorData = await res.json()
          } else {
            const text = await res.text()
            console.error('Non-JSON error response:', text.substring(0, 500))
            errorData.message = `Server error (${res.status}): ${text.substring(0, 100)}`
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError)
          errorData.message = `Server error (${res.status} ${res.statusText}). Please try again or contact support.`
        }
        alert(errorData.message || 'Failed to create payment session. Please try again or contact support.')
        console.error('Stripe session error:', errorData)
        return
      }

      // Check if response is actually JSON
      const contentType = res.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        console.error('API returned non-JSON response:', contentType)
        const text = await res.text()
        console.error('Response body:', text.substring(0, 500))
        alert('Server error: Expected JSON response but got HTML. Check console for details.')
        return
      }

      const data = await res.json()

      // Log session creation for debugging
      console.log('✅ Stripe session created:', {
        sessionId: data.sessionId,
        url: data.url,
        publishableKeyHint: data.publishableKeyHint
      })

      // Check if we have a URL
      if (!data.url) {
        console.error('❌ No checkout URL in response:', data)
        alert('Payment session created but no checkout URL available. Please contact support.')
        return
      }

      // ALWAYS use direct URL redirect - NO Stripe.js needed!
      // This completely avoids the 401 error because we don't use publishable keys at all
      console.log('✅ Redirecting directly to Stripe checkout URL (no Stripe.js needed):', data.url)
      window.location.href = data.url
    } catch (e: any) {
      console.error('Stripe payment error:', e)
      
      // Provide more specific error messages
      let errorMessage = 'Payment could not be started'
      
      if (e instanceof TypeError && e.message.includes('fetch')) {
        errorMessage = 'Network error: Unable to connect to payment server. Please check your internet connection and try again.'
      } else if (e.message) {
        errorMessage = `Payment error: ${e.message}`
      } else if (typeof e === 'string') {
        errorMessage = `Payment error: ${e}`
      }
      
      alert(errorMessage)
    }
  }

  // Start Stripe Checkout for next period / renewal (from My Tenancies)
  const startStripePaymentForRenewal = async (applicationId: string, amount: number, _propertyTitle?: string) => {
    try {
      if (!amount || amount <= 0) {
        alert('Invalid payment amount. Please contact support.')
        return
      }
      const token = localStorage.getItem('authToken')
      if (!token) {
        alert('Please log in to make a payment')
        return
      }
      const apiUrl = getApiUrl()
      const res = await fetch(`${apiUrl}/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          applicationId,
          amount: Number(amount),
          currency: (localStorage.getItem('landlordnoagent:currency') === 'NGN' ? 'ngn' : 'ngn')
        })
      })
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Failed to create payment session' }))
        alert(errorData.message || 'Failed to create payment session. Please try again.')
        return
      }
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        alert('Payment session created but no checkout URL available. Please contact support.')
      }
    } catch (e: any) {
      console.error('Renewal payment error:', e)
      alert(e?.message || 'Payment could not be started.')
    }
  }
  
  const handleLogout = () => {
    // Use centralized logout from AuthContext
    logout()
  }

  const handleProfileUpdate = async () => {
    // Refresh user data after profile update
    try {
      await refreshUser()
    } catch (error) {
      console.error('Error refreshing user data:', error)
    }
  }

  // Handle application form
  const handleApplicationSuccess = () => {
    setShowApplicationForm(false)
    setSelectedProperty(null)
    // Optionally switch to applications tab
    setActiveTab('applications')
    refreshAll() // Reload all data after new application
  }

  const handleApplicationCancel = () => {
    setShowApplicationForm(false)
    setSelectedProperty(null)
  }

  const handleApplyToProperty = (property: Property) => {
    setSelectedProperty(property)
    setShowApplicationForm(true)
  }

  // Save/unsave property
  const handleToggleSaved = async (propertyId: string) => {
    if (!user) return
    
    setSavingProperty(propertyId)
    try {
      await toggleSavedProperty(propertyId)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setSavingProperty(null)
    }
  }

  // Properties are now filtered by PropertySearch component

  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  // Format duration - unused
  // const formatDuration = (months: number) => {
  //   if (months === 12) return '1 year'
  //   if (months === 6) return '6 months'
  //   if (months === 18) return '18 months'
  //   if (months === 24) return '2 years'
  //   return `${months} months`
  // }

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.kyc_data?.firstName && user?.kyc_data?.lastName) {
      return `${user.kyc_data.firstName} ${user.kyc_data.lastName}`
    }
    return user?.email || 'User'
  }

  // Get landlord display name
  const getLandlordDisplayName = (landlord: User | null | undefined) => {
    if (!landlord) return 'Unknown Landlord'
    if (landlord.kyc_data?.firstName && landlord.kyc_data?.lastName) {
      return `${landlord.kyc_data.firstName} ${landlord.kyc_data.lastName}`
    }
    return landlord.email || 'Verified Landlord'
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-stone-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-6 text-stone-600 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <OnboardingFlow activeTab={activeTab} onTabChange={setActiveTab} userRole="client" />
      {/* Sidebar */}
      <ClientSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onLogout={handleLogout}
        user={user}
        stats={stats}
      />

      {/* Main Content - pt on mobile to clear hamburger */}
      <div className="flex-1 lg:ml-0 pt-14 sm:pt-0 min-w-0">
        {/* Header - pl on mobile so title doesn't sit under menu button */}
        <header className="bg-white shadow-sm border-b border-stone-200 sticky top-0 z-10">
          <div className="pl-14 sm:pl-4 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center py-4 sm:py-5">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-display text-stone-900 truncate">
                  {activeTab === 'browse' ? 'Browse Properties' : activeTab === 'settings' ? 'Notification Preferences' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                </h1>
                <p className="text-stone-600 mt-0.5 sm:mt-1 text-sm sm:text-base">
                  {activeTab === 'browse' && 'Find your perfect rental home'}
                  {activeTab === 'applications' && 'Track your rental applications and their status'}
                  {activeTab === 'saved' && 'Your saved properties'}
                  {activeTab === 'profile' && 'Manage your profile and verification'}
                  {activeTab === 'payments' && 'Payment history and receipts'}
                  {activeTab === 'tenancies' && 'Rent periods and expiration dates'}
                  {activeTab === 'reviews' && 'Your reviews and ratings'}
                  {activeTab === 'settings' && 'Manage notification preferences'}
                  {!['browse', 'applications', 'saved', 'profile', 'payments', 'tenancies', 'reviews', 'settings'].includes(activeTab) && 'Find your perfect rental home'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 shrink-0">
                {user?.id && <NotificationSystem userId={user.id} />}
                <CurrencySwitcher />
                <div className="text-left sm:text-right min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-stone-900 truncate">{getUserDisplayName()}</p>
                  <p className="text-[11px] sm:text-xs text-stone-500 truncate max-w-[180px] sm:max-w-none">{user.email}</p>
                  <div className="mt-1">
                    <VerificationBadge user={user} size="sm" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Application Form Modal */}
          {showApplicationForm && selectedProperty && (
            <ApplicationForm
              property={selectedProperty}
              onSuccess={handleApplicationSuccess}
              onCancel={handleApplicationCancel}
              isOpen={showApplicationForm}
            />
          )}

          {/* Browse Properties Tab */}
          {activeTab === 'browse' && (
            <div data-onboarding="browse-properties">
              {/* Advanced Search and Filter */}
              <PropertySearch 
                onPropertiesChange={(newProperties) => {
                  // Update displayed properties from search results
                  setDisplayedProperties(newProperties)
                }}
                onLoadingChange={(_isLoading) => {
                  // Loading state is managed by PropertySearch component
                }}
              />

              {/* Properties Grid */}
              <div className="mb-8">
                
                {loading.properties ? (
                  <PropertyListSkeleton count={6} />
                ) : (displayedProperties.length > 0 ? displayedProperties : properties).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-onboarding="property-card">
                    {(displayedProperties.length > 0 ? displayedProperties : properties).map((property: PropertyWithLandlord) => (
                      <div key={property.id} className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        {/* Property Image */}
                        <div className="h-48 bg-gray-200 relative overflow-hidden property-image-container">
                          {property.images && property.images.length > 0 ? (
                            <img
                              src={property.images[0]}
                              alt={property.title}
                              className="w-full h-full object-cover transition-transform hover:scale-105"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder-property.svg'
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                          )}
                          <div className="absolute top-3 right-3 space-y-1.5">
                            <div className="bg-emerald-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold border border-emerald-700 shadow-sm">
                            ✅ Verified
                            </div>
                            <div className={`${property.is_available === false ? 'bg-red-600' : 'bg-emerald-600'} text-white text-xs px-2.5 py-1 rounded-full font-semibold border ${property.is_available === false ? 'border-red-700' : 'border-emerald-700'} shadow-sm`}>
                              {property.is_available === false ? 'Unavailable' : 'Available'}
                            </div>
                            {property.last_paid_at && (
                              <div className="bg-amber-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold border border-amber-700 shadow-sm">
                                💳 Paid {property.paid_months || 0} mo{(property.paid_months || 0) === 1 ? '' : 's'}{property.paid_until ? ` • until ${property.paid_until}` : ''}
                              </div>
                            )}
                          </div>
                          {/* Save Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleToggleSaved(property.id)
                            }}
                            disabled={savingProperty === property.id}
                            className="absolute top-3 left-3 p-2.5 rounded-full bg-white bg-opacity-95 hover:bg-opacity-100 transition-all duration-200 shadow-lg hover:shadow-xl border border-stone-200"
                            title={savedProperties.includes(property.id) ? 'Remove from saved' : 'Save property'}
                          >
                            {savingProperty === property.id ? (
                              <svg className="w-5 h-5 animate-spin text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            ) : savedProperties.includes(property.id) ? (
                              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5 text-gray-600 hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                              </svg>
                            )}
                          </button>
                        </div>

                        {/* Property Details */}
                        <div className="p-6">
                          <h3 className="text-lg font-display text-stone-900 mb-2">{property.title}</h3>
                          <p className="text-stone-600 text-sm mb-3">{property.location}</p>
                          <p className="text-stone-700 text-sm mb-4 line-clamp-2">{property.description}</p>
                          
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <p className="text-2xl font-display text-emerald-600">{formatPrice(property.price)}</p>
                              <p className="text-xs text-stone-500">
                                {property.rentalType === 'short-term' ? 'per day' : 'per month'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-stone-900">
                                {property.rentalType === 'short-term' ? 'Short-term (Shortlet)' : 'Long-term'}
                              </p>
                              <p className="text-xs text-stone-500">rental type</p>
                            </div>
                          </div>

                          <div className="border-t border-stone-200 pt-4">
                            <div className="flex justify-between items-center text-sm text-stone-500 mb-3">
                              <span>Landlord: {getLandlordDisplayName(property.landlord)}</span>
                              <span>{property.images?.length || 0} photos</span>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => navigate(`/property/${property.id}`)}
                                className="flex-1 bg-stone-900 text-white py-2.5 px-4 rounded-xl hover:bg-stone-800 transition-all duration-300 hover:shadow-lg hover:shadow-stone-500/25 font-semibold text-sm"
                              >
                                View Details
                              </button>
                              {acceptedByPropertyId[property.id] ? (
                                paymentStatusByPropertyId[property.id]?.paid ? (
                                  <button
                                    className="flex-1 py-2.5 px-4 rounded-xl transition-all duration-300 text-sm bg-emerald-600 text-white cursor-default font-semibold"
                                    disabled
                                    title="Payment completed"
                                  >
                                    Paid
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => startStripePayment(acceptedByPropertyId[property.id], property)}
                                    className={`flex-1 py-2.5 px-4 rounded-xl transition-all duration-300 text-sm font-semibold ${
                                      property.is_available !== false
                                        ? 'bg-amber-600 text-white hover:bg-amber-700 hover:shadow-lg hover:shadow-amber-500/25'
                                        : 'bg-stone-400 text-white cursor-not-allowed'
                                    }`}
                                    disabled={property.is_available === false}
                                    title={property.is_available === false ? 'This property is currently unavailable' : 'Proceed to Stripe payment'}
                                  >
                                    {property.is_available === false ? 'Unavailable' : 'Pay Now'}
                                  </button>
                                )
                              ) : (
                                <button
                                  onClick={() => user.is_verified && property.is_available !== false && handleApplyToProperty(property)}
                                  className={`flex-1 py-2.5 px-4 rounded-xl transition-all duration-300 text-sm font-semibold ${
                                    user.is_verified && property.is_available !== false
                                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-500/25' 
                                      : 'bg-stone-400 text-white cursor-not-allowed'
                                  }`}
                                  disabled={!user.is_verified || property.is_available === false}
                                  title={!user.is_verified 
                                    ? 'Complete KYC verification to apply' 
                                    : property.is_available === false 
                                      ? 'This property is currently unavailable' 
                                      : ''}
                                >
                                  {user.is_verified 
                                    ? (property.is_available === false ? 'Unavailable' : 'Inquire Now') 
                                    : 'KYC Required'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={
                      <svg className="w-16 h-16 text-stone-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    }
                    title="No Properties Found"
                    description={
                      displayedProperties.length === 0 && properties.length > 0
                        ? "No properties match your current search filters. Try adjusting your filters to see more results."
                        : "No verified properties are currently available. Check back soon or adjust your search criteria."
                    }
                    action={
                      displayedProperties.length === 0 && properties.length > 0
                        ? {
                            label: 'Clear Filters',
                            onClick: () => {
                              // This would be handled by PropertySearch component
                              // For now, just show the action
                            }
                          }
                        : undefined
                    }
                  />
                )}
              </div>

            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <ApplicationStatus userId={user.id} />
          )}

          {/* Saved Properties Tab */}
          {activeTab === 'saved' && (
            <SavedProperties userId={user.id} />
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {config.features.kycStepper ? (
                <KycStepper user={user} isVerified={user?.is_verified === true} onCompleted={handleProfileUpdate} />
              ) : (
                <UserProfile user={user} onUpdate={handleProfileUpdate} />
              )}
              <div className="border-t border-stone-200 pt-6">
                <NotificationPreferences userId={user.id} onUpdate={handleProfileUpdate} />
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              <PaymentInterface clientId={user.id} />
              <Suspense fallback={<PaymentHistorySkeleton />}>
              <PaymentHistory clientId={user.id} />
              </Suspense>
            </div>
          )}

          {/* My Tenancies Tab */}
          {activeTab === 'tenancies' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-display text-stone-900">My Tenancies</h2>
                <p className="text-stone-600 mt-1">Track rent payment periods and see the exact rent expiration date. Pay for the next period to renew when about to expire.</p>
              </div>
              <MyTenancies
                tenancies={tenancies}
                loading={loading.tenancies ?? false}
                onRefresh={loadTenancies}
                onPayNextPeriod={(applicationId, _propertyTitle, amount) => startStripePaymentForRenewal(applicationId, amount)}
              />
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <ReviewRatingSystem clientId={user.id} />
            </div>
          )}

          {/* Notification Preferences / Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <NotificationPreferences userId={user.id} onUpdate={handleProfileUpdate} />
            </div>
          )}

        </main>
      </div>
    </div>
  )
}

export default function ClientDashboard() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-stone-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <p className="mt-6 text-stone-600 font-medium">Loading Dashboard...</p>
          </div>
        </div>
      }
    >
      <ClientDashboardContent />
    </Suspense>
  )
} 

